import { useEffect, useRef } from 'react';
import divider from '../images/divider-new-design.svg';
import blockDivider from '../images/block-divider.png';
import homeBackground from '../images/home-background.png';
import sphere from '../images/sphere.png';
import '../styles/Home.scss';
import intro from '../video/alnair-promo.mp4';
import { HomeUseCasesBlock } from './HomeUseCasesBlock';
import 'animate.css';
import { HomeStoryBehind } from './HomeStoryBehind';
import { useInView } from 'react-intersection-observer';
import { Row, Col } from 'react-simple-flex-grid';
import { HomeTeamSlider } from './HomeTeamSlider';

function Home(props: { title: string }) {
  const vidRef = useRef<any>(null);

  useEffect(() => {
    document.title = props.title;
  });

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });

  const [alnairSectionRef, alnairSectionInView] = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });

  const [howItWorksSectionRef, howItWorksSectionInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const promoLink =
    'https://www.youtube.com/watch?v=tm029a824qM&list=UULFVYd_qsRRTZFiRH8lPJoU3w&index=45&ab_channel=SoftServe';

  const openVideo = (link: string) => {
    window.open(link, '_blank');
  };

  const scrollToAlnairSection = () => {
    const navbarHeight = window.innerWidth <= 992 ? 80 : 100;

    const targetElement = document.querySelector(
      '.alnair-section'
    ) as HTMLElement;

    if (targetElement) {
      const targetPosition = targetElement.offsetTop - navbarHeight;

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <div className="home-page">
        <div
          ref={ref}
          className={inView ? 'background-image slideInUp' : 'background-image'}
        >
          <div className="section-shadow section-shadow-from-top"></div>
          <img src={homeBackground} alt="Alnair"></img>
          <div className="section-shadow-wrapper"></div>
          <div className="know-your-future">
            <Row align="bottom">
              <Col xs={12}>
                <h1 className="future-title gl-title">
                  Experience the Future — <span>Now</span>
                </h1>
                <p className="future-question">
                  Let Alnair guide you to your tech-driven future.
                </p>
              </Col>
              <Col span={0} sm={12}>
                <div
                  className="discover-wrapper"
                  onClick={scrollToAlnairSection}
                >
                  <span className="discover-title">Discover</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="30"
                    height="14"
                    viewBox="0 0 30 14"
                    fill="none"
                  >
                    <path
                      d="M2 2L15.65 11L28 2"
                      stroke="white"
                      strokeWidth="4"
                    />
                  </svg>
                </div>
              </Col>
            </Row>
          </div>
          <div className="section-shadow section-shadow-from-bottom"></div>
        </div>
        <div
          ref={alnairSectionRef}
          className={
            alnairSectionInView ? 'alnair-section slideInUp' : 'alnair-section'
          }
        >
          <div className="section-shadow section-shadow-from-top"></div>
          <img className="divider" src={divider} alt="divider" />
          <div className="alnair-description-container">
            <h3 className="alnair-title">ALNAIR</h3>
            <div className="alnair-raw">
              <div className="alnair-col">
                SoftServe’s immersive experience team, in collaboration with the
                R&D department, developed Alnair — a Gen AI-powered multi-modal
                immersive experience.
                <br />
                <br />
                Alnair demonstrates how advanced technologies are combined to
                expand the possibilities of interactive experiences.
              </div>
              <div className="alnair-col">
                Guided by an AI fortune teller, users co-create a planet using
                Gen AI, Gen AI-enabled avatars, and haptics. The experience ends
                with users receiving a personalized, printed guardian badge with
                their name and role in their new world.
              </div>
            </div>
          </div>

          <div className="alnair-video-wrapper">
            <video
              className="alnair-video"
              ref={vidRef}
              autoPlay
              playsInline
              muted
              loop
            >
              <source src={intro} type="video/mp4" />
            </video>
            <div className="promo-video-footer">
              <div className="footer-actions">
                <h5 className="footer-title">Watch our video</h5>
                <div
                  className="play-button"
                  onClick={() => openVideo(promoLink)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                  >
                    <circle cx="20" cy="20" r="19.5" stroke="white" />
                    <path
                      d="M25.5 19.634C26.1667 20.0189 26.1667 20.9811 25.5 21.366L18.75 25.2631C18.0833 25.648 17.25 25.1669 17.25 24.3971L17.25 16.6029C17.25 15.8331 18.0833 15.352 18.75 15.7369L25.5 19.634Z"
                      fill="white"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="section-shadow section-shadow-from-bottom"></div>
        </div>

        <div
          ref={howItWorksSectionRef}
          className={
            howItWorksSectionInView
              ? 'how-it-works-background slideInUp'
              : 'how-it-works-background'
          }
        >
          <div>
            <div className="section-shadow section-shadow-from-top"></div>
            <h2 className="how-it-works-title">What makes Alnair work?</h2>
            <p className="how-it-works-description">
              SoftServe applies next-gen technologies for today’s solutions.
            </p>
            <div className="how-blocks-container">
              <div className="how-block">
                <h3 className="block-title">
                  <span>#1</span> Generative AI
                </h3>
                <p className="block-text">
                  Gen AI does more than just enable Alnair to communicate with
                  users. It also facilitates face-swaps, creates new avatars,
                  and generates personalized names and professions.
                  <br />
                  After giving consent, users also receive a personalized
                  guardian badge at the end.
                </p>
              </div>
              <img src={blockDivider} alt="Divider"></img>
              <div className="how-block">
                <h3 className="block-title">
                  <span>#2</span> High-Fidelity Animation
                </h3>
                <p className="block-text">
                  Users transport themselves into a parallel universe with
                  high-fidelity, digital avatars built with the MetaHuman
                  Creator and Unreal Engine 5 — instantly rendered, completely
                  unique, and interaction-ready.
                </p>
              </div>
              <img src={blockDivider} alt="Divider"></img>
              <div className="how-block">
                <h3 className="block-title">
                  <span>#3</span> Mid-Air Haptics
                </h3>
                <p className="block-text">
                  Alnair blurs the line between reality and fiction, just as the
                  future of technology will bring the digital world into the
                  physical. Life-like sensations take users into Alnair’s world
                  — just as Alnair enters theirs.
                </p>
              </div>
            </div>
            <div className="section-shadow section-shadow-from-bottom"></div>
          </div>
          <img alt="sphere" src={sphere} className="sphere-img" />
        </div>

        <HomeUseCasesBlock />
        <HomeTeamSlider />
        <HomeStoryBehind isCardContainer />
      </div>
    </>
  );
}

export default Home;
